/**
 * Wizard Step 4 — Professionals.
 *
 * Four separate intro screens collapsed into one multi-select.
 * Same pattern as Household: uncheck = decline, check = priority.
 */
import React, { useEffect, useState } from 'react';
import { View } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { WizardShell } from '../components/WizardShell';
import { WizardCheckOption } from '../components/WizardCheckOption';
import { useWizard } from '../WizardContext';
import { analytics } from '../../../services/analytics/analytics';
import { AnalyticsEvent } from '../../../services/analytics/events';

const WIZARD_STEP = 4;
const TOTAL_STEPS = 6;

export const WizardProfessionalsScreen: React.FC = () => {
  const navigation = useNavigation<any>();
  const { answers, setProfessionals } = useWizard();
  const [hasPhysician, setHasPhysician] = useState(answers.professionals.hasPhysician);
  const [hasAttorney, setHasAttorney] = useState(answers.professionals.hasAttorney);
  const [hasInvestmentAdvisor, setHasInvestmentAdvisor] = useState(answers.professionals.hasInvestmentAdvisor);
  const [hasAccountant, setHasAccountant] = useState(answers.professionals.hasAccountant);

  useEffect(() => {
    analytics.track(AnalyticsEvent.WizardStepShown, { step: 'professionals', number: WIZARD_STEP });
  }, []);

  const handleContinue = () => {
    setProfessionals({ hasPhysician, hasAttorney, hasInvestmentAdvisor, hasAccountant });
    analytics.track(AnalyticsEvent.WizardStepCompleted, {
      step: 'professionals',
      has_physician: hasPhysician,
      has_attorney: hasAttorney,
      has_investment_advisor: hasInvestmentAdvisor,
      has_accountant: hasAccountant,
    });
    navigation.navigate('WizardDocuments');
  };

  return (
    <WizardShell
      stepNumber={WIZARD_STEP}
      totalSteps={TOTAL_STEPS}
      title="Which professionals do you work with?"
      subtitle="If something happens, your loved ones may need to reach these people. Check the ones you'd want them to have."
      primaryLabel="Continue"
      onPrimaryPress={handleContinue}
      onBackPress={() => navigation.goBack()}
    >
      <View>
        <WizardCheckOption
          label="Primary care physician"
          description="Name, phone, and any ongoing treatments"
          selected={hasPhysician}
          onToggle={() => setHasPhysician(v => !v)}
        />
        <WizardCheckOption
          label="Attorney"
          description="Estate, family, or business counsel"
          selected={hasAttorney}
          onToggle={() => setHasAttorney(v => !v)}
        />
        <WizardCheckOption
          label="Investment advisor"
          description="Financial planner, wealth manager, or broker"
          selected={hasInvestmentAdvisor}
          onToggle={() => setHasInvestmentAdvisor(v => !v)}
        />
        <WizardCheckOption
          label="Accountant"
          description="CPA or tax preparer"
          selected={hasAccountant}
          onToggle={() => setHasAccountant(v => !v)}
        />
      </View>
    </WizardShell>
  );
};
